import UserRoleTypeEnum from "@/constants/enums/UserRoleTypeEnum";
import { CurrentCompany, User, UserRoleType } from "@/types";

/** Every assignable role, in enum order. Use this instead of hardcoding role lists. */
export const ALL_ROLE_TYPES = Object.values(UserRoleTypeEnum) as UserRoleType[];

/** Roles that grant write access. Everything else is read-only. */
export const WRITE_ROLE_TYPES: UserRoleType[] = [UserRoleTypeEnum.ADMIN, UserRoleTypeEnum.USER];

/**
 * The single source of truth for "which roles does this user hold in this company".
 * Prefers user.role[], falling back to the company payload while the user query is in flight.
 */
export const getRolesForCompany = (user?: User, company?: CurrentCompany): UserRoleType[] => {
  if (!company?.companyId) return [];

  const fromUser = (user?.role ?? [])
    .filter((role) => role.companyId === company.companyId)
    .map((role) => role.roleType);

  if (fromUser.length > 0) return Array.from(new Set(fromUser));

  return company.roleTypes ?? [];
};

/**
 * True only when the roles are known AND at least one grants write access.
 * Fails closed: unknown roles are not treated as write-capable, so this is the
 * predicate to use when filtering members down to "who can be assigned work".
 */
export const hasWriteRole = (roles: UserRoleType[] = []) => roles.some((role) => WRITE_ROLE_TYPES.includes(role));

/**
 * Read-only means "holds roles, but none of them grant write access".
 *
 * Fails open: an empty array is deliberately NOT read-only, because roles are unknown
 * while the user and company queries load and treating that as read-only would flash a
 * limited UI. That makes this predicate correct for the *signed-in* user only — never
 * negate it to filter a loaded list, or an unknown-role member slips through as
 * write-capable. Use `hasWriteRole` for that.
 */
export const isReadOnlyRoles = (roles: UserRoleType[] = []) => roles.length > 0 && !hasWriteRole(roles);
